import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { FormField } from '../../components/ui/FormField';
import { CheckIcon } from '../../components/ui/Icons';
import { AuthFormError, authErrorMessage } from '../../components/layout/AuthFormError';
import { useRegisterAccount } from '../../hooks/useRegisterAccount';
import { useToastStore } from '../../store/toastStore';
import { formatPhoneMask } from '../../utils/phone';
import {
  MIN_PASSWORD_LENGTH,
  RegisterFormErrors,
  RegisterFormState,
  validateRegisterForm,
} from '../../utils/registerValidation';
import { OrderConfirmationGuest } from './checkoutStorage';
import { OUTLINE_LINK_BUTTON_CLASS } from './styles';

const BENEFITS = [
  'Acompanhar seus pedidos',
  'Ver seu saldo de crédito e vale-troca',
  'Favoritos em qualquer aparelho',
  'Tamanho certo para a sua criança',
];

interface CreateAccountFormState extends RegisterFormState {
  confirmPassword: string;
}

type CreateAccountErrors = RegisterFormErrors & { confirmPassword?: string };

interface CreateAccountCardProps {
  guest?: OrderConfirmationGuest;
  created: boolean;
  onCreated: () => void;
}

function buildInitialForm(guest?: OrderConfirmationGuest): CreateAccountFormState {
  return {
    name: guest?.name ?? '',
    email: guest?.email ?? '',
    phone: formatPhoneMask(guest?.phone ?? ''),
    password: '',
    confirmPassword: '',
  };
}

function validateCreateAccount(form: CreateAccountFormState): CreateAccountErrors {
  const errors: CreateAccountErrors = validateRegisterForm(form);
  if (!errors.password && form.password !== form.confirmPassword) {
    errors.confirmPassword = 'As senhas não são iguais.';
  }
  return errors;
}

export function CreateAccountCard({ guest, created, onCreated }: CreateAccountCardProps) {
  const registerAccount = useRegisterAccount();
  const showToast = useToastStore((s) => s.show);
  const formRef = useRef<HTMLFormElement>(null);

  const [form, setForm] = useState<CreateAccountFormState>(() => buildInitialForm(guest));
  const [errors, setErrors] = useState<CreateAccountErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof CreateAccountFormState, boolean>>>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  if (created) {
    return (
      <section className="w-full bg-white rounded-2xl shadow-sm p-6 sm:p-8 mt-4 text-center flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-olivine/15 text-olivine-dark flex items-center justify-center mb-3">
          <CheckIcon className="w-6 h-6" />
        </div>
        <h2 className="text-lg font-bold text-text-dark mb-1">Sua conta está pronta</h2>
        <p className="text-base text-text-medium mb-5 max-w-sm">
          Este pedido já aparece em Meus pedidos, junto com os próximos.
        </p>
        <Link to="/pedidos" className={`${OUTLINE_LINK_BUTTON_CLASS} w-full sm:w-auto px-8`}>
          Meus pedidos
        </Link>
      </section>
    );
  }

  const updateField = (field: keyof CreateAccountFormState, value: string) => {
    const next = { ...form, [field]: value };
    setForm(next);
    setSubmitError('');
    if (touched[field]) setErrors(validateCreateAccount(next));
  };

  const handleBlur = (field: keyof CreateAccountFormState) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validateCreateAccount(form));
  };

  const fieldError = (field: keyof CreateAccountFormState) => (touched[field] ? errors[field] : undefined);

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTouched({ name: true, email: true, phone: true, password: true, confirmPassword: true });
    const validationErrors = validateCreateAccount(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      requestAnimationFrame(() => {
        formRef.current?.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus();
      });
      return;
    }

    setLoading(true);
    setSubmitError('');
    try {
      await registerAccount(form);
      showToast('Conta criada! Seus pedidos ficam em Meus pedidos.', 'success');
      onCreated();
    } catch (err) {
      setSubmitError(authErrorMessage(err, 'Não foi possível criar sua conta agora. Tente de novo.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      aria-labelledby="create-account-heading"
      className="w-full bg-white rounded-2xl shadow-sm p-6 sm:p-8 mt-4 text-left"
    >
      <h2 id="create-account-heading" className="text-lg font-bold text-text-dark">
        Crie sua conta
      </h2>
      <p className="text-base text-text-medium mt-1 mb-4">
        Leva menos de um minuto, já usamos os dados do seu pedido.
      </p>

      <ul className="grid gap-2 sm:grid-cols-2 mb-6">
        {BENEFITS.map((benefit) => (
          <li key={benefit} className="flex items-start gap-2 text-sm text-text-dark">
            <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-olivine/15 text-olivine-dark flex items-center justify-center">
              <CheckIcon className="w-3 h-3" />
            </span>
            {benefit}
          </li>
        ))}
      </ul>

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        noValidate
        data-clarity-mask="true"
        className="grid gap-4 sm:grid-cols-2"
      >
        <div className="sm:col-span-2">
          <FormField label="Nome completo" error={fieldError('name')}>
            <Input
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              onBlur={handleBlur('name')}
              placeholder="Maria Silva"
              autoComplete="name"
            />
          </FormField>
        </div>

        <FormField label="Celular" error={fieldError('phone')}>
          <Input
            type="tel"
            inputMode="numeric"
            value={form.phone}
            onChange={(e) => updateField('phone', formatPhoneMask(e.target.value))}
            onBlur={handleBlur('phone')}
            placeholder="(85) 99999-9999"
            autoComplete="tel"
          />
        </FormField>

        <FormField label="E-mail" error={fieldError('email')}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => updateField('email', e.target.value)}
            onBlur={handleBlur('email')}
            placeholder="maria@email.com"
            autoComplete="email"
            autoCapitalize="none"
          />
        </FormField>

        <FormField
          label="Senha"
          hint={fieldError('password') ? undefined : `Use pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`}
          error={fieldError('password')}
        >
          <PasswordInput
            value={form.password}
            onChange={(e) => updateField('password', e.target.value)}
            onBlur={handleBlur('password')}
            autoComplete="new-password"
          />
        </FormField>

        <FormField label="Confirmar senha" error={fieldError('confirmPassword')}>
          <PasswordInput
            value={form.confirmPassword}
            onChange={(e) => updateField('confirmPassword', e.target.value)}
            onBlur={handleBlur('confirmPassword')}
            autoComplete="new-password"
          />
        </FormField>

        {submitError && (
          <div className="sm:col-span-2">
            <AuthFormError>
              <p>{submitError}</p>
            </AuthFormError>
          </div>
        )}

        <div className="sm:col-span-2">
          <Button type="submit" fullWidth loading={loading} size="lg">
            {loading ? 'Criando conta...' : 'Criar minha conta'}
          </Button>
        </div>
      </form>
    </section>
  );
}
